
import { ref, reactive, getCurrentInstance, nextTick, onMounted } from 'vue'
import { ElMessageBox, ElMessage } from 'element-plus'
import {
  Search,
  Refresh,
  Plus,
  Sort,
  Edit,
  Delete
} from '@element-plus/icons-vue'
import formatUtcString from '@/utils/date-format'
import {
  listMenu,
  getMenu,
  delMenu,
  addMenu,
  updateMenu
} from '@/service/main/auth'
import IconSelect from '@/components/IconSelect/index.vue'
import SvgIcon from '@/components/SvgIcon/index.vue'
import type { FormInstance } from 'element-plus'

export default {
  name: 'Menu',
  components: {
    IconSelect,
    SvgIcon
  },
  setup() {
    /*************************
     * 响应式数据区
     *************************/
    const loading = ref(true)
    const showSearch = ref(true)
    const menuList = ref<any[]>([])
    const menuOptions = ref<any[]>([])
    const title = ref('')
    const open = ref(false)
    const isExpandAll = ref(false)
    const refreshTable = ref(true)

    const queryParams = reactive({
      menuName: undefined as string | undefined,
      visible: undefined as string | undefined,
      status: undefined as string | undefined
    })

    const treeSelectProps = {
      label: 'menuName',
      value: 'menuId',
      children: 'children'
    }

    const form = reactive<any>({
      menuId: undefined,
      parentId: 0,
      menuName: undefined,
      icon: undefined,
      menuType: 'M',
      orderNum: undefined,
      isFrame: '1',
      isCache: '0',
      visible: '0',
      status: '0'
    })

    const rules = {
      menuName: [{ required: true, message: '菜单名称不能为空', trigger: 'blur' }],
      orderNum: [{ required: true, message: '菜单顺序不能为空', trigger: 'blur' }],
      path: [{ required: true, message: '路由地址不能为空', trigger: 'blur' }]
    }

    /*************************
     * refs
     *************************/
    const formRef = ref<FormInstance>()
    const queryForm = ref<FormInstance>()
    const iconSelectRef = ref()

    /*************************
     * 其他上下文
     *************************/
    const { proxy } = getCurrentInstance() as any
    const dict = reactive({
      type: {
        sys_show_hide: [],
        sys_normal_disable: []
      }
    })

    // 加载字典数据
    const loadDicts = () => {
      proxy.getDicts("sys_show_hide").then((response: any) => {
        console.log('sys_show_hide response:', response)
        // 确保字典数据结构正确
        dict.type.sys_show_hide = response.data.map((item: any) => ({
          value: item.dictValue,
          label: item.dictLabel
        }))
      })
      proxy.getDicts("sys_normal_disable").then((response: any) => {
        console.log('sys_normal_disable response:', response)
        // 确保字典数据结构正确
        dict.type.sys_normal_disable = response.data.map((item: any) => ({
          value: item.dictValue,
          label: item.dictLabel
        }))
      })
    }

    // 显示图标选择器
    const showIconSelect = () => {
      nextTick(() => {
        iconSelectRef.value?.reset()
      })
    }

    /*************************
     * 工具函数
     *************************/
    const parseTime = (val: string) => formatUtcString(val)

    const handleTree = (data: any[], idKey: string) => {
      const map: Record<string, any> = {}
      data.forEach((item) => {
        map[item[idKey]] = item
      })
      const tree: any[] = []
      data.forEach((item) => {
        const parent = map[item.parentId]
        if (parent) {
          parent.children = parent.children || []
          parent.children.push(item)
        } else {
          tree.push(item)
        }
      })
      return tree
    }

    /*************************
     * 数据请求
     *************************/
    const getList = () => {
      loading.value = true
      listMenu(queryParams).then((response: any) => {
        menuList.value = handleTree(response.data, 'menuId')
        loading.value = false
      })
    }

    const getTreeselect = () => {
      listMenu({}).then((response: any) => {
        menuOptions.value = []
        const menu = { menuId: 0, menuName: '主类目', children: [] as any[] }
        menu.children = handleTree(response.data, 'menuId')
        menuOptions.value.push(menu)
      })
    }

    /*************************
     * 业务操作
     *************************/
    const reset = () => {
      Object.assign(form, {
        menuId: undefined,
        parentId: 0,
        menuName: undefined,
        icon: undefined,
        menuType: 'M',
        orderNum: undefined,
        isFrame: '1',
        isCache: '0',
        visible: '0',
        status: '0'
      })
      formRef.value && formRef.value.clearValidate()
    }

    const cancel = () => {
      open.value = false
      reset()
    }

    const handleQuery = () => {
      getList()
    }

    const resetQuery = () => {
      queryForm.value && queryForm.value.resetFields()
      handleQuery()
    }

    const selected = (name: string) => {
      form.icon = name
    }

    const handleAdd = (row?: any) => {
      reset()
      getTreeselect()
      form.parentId = row && row.menuId ? row.menuId : 0
      open.value = true
      title.value = '添加菜单'
    }

    const toggleExpandAll = () => {
      refreshTable.value = false
      isExpandAll.value = !isExpandAll.value
      nextTick(() => {
        refreshTable.value = true
      })
    }

    const handleUpdate = (row: any) => {
      reset()
      getTreeselect()
      getMenu(row.menuId).then((response: any) => {
        Object.assign(form, response.data)
        open.value = true
        title.value = '修改菜单'
      })
    }

    const submitForm = () => {
      formRef.value?.validate((valid: boolean) => {
        if (valid) {
          const request = form.menuId !== undefined ? updateMenu(form) : addMenu(form)
          request.then(() => {
            ElMessage.success(form.menuId !== undefined ? '修改成功' : '新增成功')
            open.value = false
            getList()
          })
        }
      })
    }

    const handleDelete = (row: any) => {
      ElMessageBox.confirm(
        `是否确认删除名称为"${row.menuName}"的数据项？`,
        '提示',
        { type: 'warning' }
      )
        .then(() => delMenu(row.menuId))
        .then(() => {
          getList()
          ElMessage.success('删除成功')
        })
        .catch(() => {
          ElMessage.error('删除失败')
        })
    }

    /*************************
     * 生命周期
     *************************/
    onMounted(() => {
      getList()
      loadDicts()
    })

    return {
      loading,
      showSearch,
      menuList,
      menuOptions,
      title,
      open,
      isExpandAll,
      refreshTable,
      queryParams,
      treeSelectProps,
      form,
      rules,
      formRef,
      queryForm,
      iconSelectRef,
      dict,
      parseTime,
      showIconSelect,
      getList,
      getTreeselect,
      reset,
      cancel,
      handleQuery,
      resetQuery,
      selected,
      handleAdd,
      toggleExpandAll,
      handleUpdate,
      submitForm,
      handleDelete,
      loadDicts,
      // 图标
      Search,
      Refresh,
      Plus,
      Sort,
      Edit,
      Delete
    }
  }
}
